import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';

import { ToastService } from '../../../../../../services/toast.service';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { CampaignModel } from '../../../../../../types/campaign.interface';
import { EntityDefinition } from '../../../../../../types/entity-definition.interface';
import { MenuItemWithIcon } from '../../../../../../types/menu.interface';
import { PermissionScope } from '../../../../../../types/permission-scope.enum';
import { assetWriteSignal } from '../../../../../../utils/asset-permissions.utils';
import { assetIdSignalFromRoute } from '../../../../../../utils/asset-route.utils';
import { buildEntityMenuItems } from '../../../../../../utils/entity-menu.utils';
import { GenericParentGridComponent } from '../../../../../generic/generic-parent-grid.component';
import { CesAssetBreadcrumbComponent } from '../../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonComponent } from '../../../../../ui/ces-buttons/ces-button.component';
import { CesPopupMenuComponent } from '../../../../../ui/ces-popup-menu/ces-popup-menu.component';
import { CesTitleComponent } from '../../../../../ui/ces-route-title/ces-route-title.component';
import { CampaignModelActionsService } from '../sub-services/campaign-model-actions.service';
import { CampaignNavigationService } from '../sub-services/campaign-navigation.service';

@Component({
  selector: 'app-campaign-models-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesAssetBreadcrumbComponent,
    CesButtonComponent,
    CesTitleComponent,
    CesPopupMenuComponent,
    GenericParentGridComponent,
    TranslocoModule,
  ],
  templateUrl: './campaign-models-list.component.html',
})
export class CampaignModelsListComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly actions = inject(CampaignModelActionsService);
  private readonly toastService = inject(ToastService);
  private readonly navigationService = inject(CampaignNavigationService);
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());

  readonly actionMenu = viewChild.required<CesPopupMenuComponent>('actionMenu');
  readonly selectedModel = signal<CampaignModel | null>(null);
  readonly isLoading = signal(true);

  readonly currentAssetId = assetIdSignalFromRoute(this.route);
  private readonly canWriteAsset = assetWriteSignal(PermissionScope.Campaigns, this.currentAssetId);

  constructor() {
    effect(() => {
      const assetId = this.currentAssetId();
      if (assetId) void this.loadCampaignModels(assetId);
    });
  }

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    const baseItems = this.navigationService.breadcrumbTrail();
    return [...baseItems, { label: this.translate.translate('navigation.models') }];
  });

  readonly campaignModels = computed(() => (this.currentAssetId() ? this.campaignModelStore.entities() : []));

  readonly tableDefinition = computed<EntityDefinition<CampaignModel>>(() => {
    const assetId = this.currentAssetId() ?? undefined;
    return {
      entityType: 'CampaignModel',
      enableFiltering: true,
      defaultSortField: 'name',
      defaultSortOrder: 1,
      list: {
        columns: [
          {
            field: 'name',
            headerKey: 'entities.campaign.models.name',
            sortable: true,
            filterable: true,
            class: 'w-full min-w-[200px]',
            contentClass: 'line-clamp-1',
            showTooltip: true,
          },
        ],
      },
      fields: [],
      rowLinkHref: model => (assetId ? ['/assets', assetId, 'monitoring', 'campaigns', 'models', model.id] : null),
      actionButtons: {
        parent: {
          add: false,
          edit: false,
          delete: false,
          custom: [
            {
              icon: 'more_vert',
              action: 'menu',
              tooltipKey: 'common.labels.actions',
              tooltipPosition: 'bottom',
              hasPermission: { method: 'putCampaignModelDetailResource', assetId },
            },
          ],
        },
      },
      messages: {
        dialogTitles: {
          create: () => this.translate.translate('common.buttons.add'),
          edit: () => this.translate.translate('entities.campaign.models.editTooltip'),
          delete: () => this.translate.translate('entities.campaign.models.deleteTooltip'),
        },
        buttons: {
          add: () => this.translate.translate('common.buttons.add'),
          create: () => this.translate.translate('common.buttons.add'),
          edit: () => this.translate.translate('common.buttons.edit'),
          delete: () => this.translate.translate('common.buttons.delete'),
        },
      },
    };
  });

  readonly menuItems = computed((): MenuItemWithIcon[] => {
    this.translations();
    return buildEntityMenuItems(
      this.translate,
      'common.buttons',
      {
        onEdit: () => void this.onRenameModel(),
        onDuplicate: () => void this.onDuplicateModel(),
        onDelete: () => void this.onDeleteModel(),
      },
      { canWrite: this.canWriteAsset() },
    );
  });

  onRowAction(action: string, model: CampaignModel, event?: MouseEvent): void {
    if (action === 'menu' && event) {
      this.selectedModel.set(model);
      this.actionMenu().toggle(event);
    }
  }

  private async loadCampaignModels(assetId: number): Promise<void> {
    this.isLoading.set(true);
    try {
      await this.campaignModelStore.getByAssetId(assetId);
    } catch {
      this.toastService.error('common.toasts.loadFailed');
    } finally {
      this.isLoading.set(false);
    }
  }

  async onCreateModel(): Promise<void> {
    const assetId = this.currentAssetId();
    if (!assetId) return;
    const created = await this.actions.create(assetId);
    if (created) {
      void this.router.navigate(['/assets', assetId, 'monitoring', 'campaigns', 'models', created.id, 'measures']);
    }
  }

  private modelNames(): string[] {
    return this.campaignModels().map(m => m.name ?? '');
  }

  private async onRenameModel(): Promise<void> {
    const model = this.selectedModel();
    if (model) await this.actions.rename(model);
  }

  private async onDuplicateModel(): Promise<void> {
    const model = this.selectedModel();
    const assetId = this.currentAssetId();
    if (!model) return;
    const copy = await this.actions.duplicate(model, this.modelNames());
    if (copy && assetId) {
      void this.router.navigate(['/assets', assetId, 'monitoring', 'campaigns', 'models', copy.id, 'measures']);
    }
  }

  private async onDeleteModel(): Promise<void> {
    const model = this.selectedModel();
    if (model) await this.actions.delete(model);
  }
}
